import { type useFormSetValue } from '@framework/form/hooks/useFormSetValue.ts';
import { type useTriggerComputedSync } from '@framework/form/hooks/useTriggerComputedSync.ts';

export interface IApplyMultiSelectFormValuesParams {
  setValue: ReturnType<typeof useFormSetValue>;
  triggerSync: ReturnType<typeof useTriggerComputedSync>;
  objectFieldName: string;
  values: Record<string, boolean>;
  shouldDirty?: boolean;
}

/**
 * Applica i valori delle opzioni multi-select ai rispettivi campi del form.
 *
 * Ogni `CheckboxOptionInput` registra un `useController` sul path annidato
 * `${objectFieldName}.${optionId}`: chiamare `setValue` sul padre non
 * notifica i controller figli, quindi è necessario aggiornare ogni path
 * individualmente per triggerare i re-render delle checkbox.
 *
 * Trattandosi di un batch update su N campi, disabilitiamo il sync interno
 * di ogni `setValue` (`shouldSyncComputedValues: false`) e triggeriamo
 * manualmente una singola `triggerSync()` finale. Altrimenti lanceremmo un
 * `syncComputedValues` (~50ms) per ogni opzione: con 100 opzioni sarebbero
 * ~5s di lag. La modifica parte da N campi insieme, quindi non esiste "il"
 * path originante e `triggerSync()` viene chiamato senza event.
 *
 * `setValue` è il wrapper `useFormSetValue`, `triggerSync` è
 * `useTriggerComputedSync`.
 */
export function applyMultiSelectFormValues({
  setValue,
  triggerSync,
  objectFieldName,
  values,
  shouldDirty = true,
}: IApplyMultiSelectFormValuesParams): void {
  const entries = Object.entries(values);
  for (const [key, value] of entries) {
    setValue(`${objectFieldName}.${key}`, value === true, {
      shouldDirty,
      shouldSyncComputedValues: false,
    });
  }
  if (entries.length > 0) {
    triggerSync();
  }
}
